const ALLOWED_TAGS = new Set(['B', 'I', 'SPAN']);
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED']);
const SAFE_CLASS = /^[\w\s\-:/[\].%,#]+$/;

function isSafeClass(value: string): boolean {
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 200 && SAFE_CLASS.test(trimmed);
}

function cloneSanitized(node: Node): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const el = node as Element;
  if (DROP_TAGS.has(el.tagName)) return null;

  if (!ALLOWED_TAGS.has(el.tagName)) {
    const frag = document.createDocumentFragment();
    appendSanitizedChildren(el, frag);
    return frag.childNodes.length > 0 ? frag : null;
  }

  const clean = document.createElement(el.tagName.toLowerCase());
  if (el.tagName === 'SPAN') {
    const cls = el.getAttribute('class');
    if (cls && isSafeClass(cls)) {
      clean.setAttribute('class', cls.trim());
    }
  }

  appendSanitizedChildren(el, clean);
  return clean;
}

function appendSanitizedChildren(source: Node, target: ParentNode): void {
  for (const child of source.childNodes) {
    const sanitized = cloneSanitized(child);
    if (!sanitized) continue;
    if (sanitized.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      target.append(...(sanitized as DocumentFragment).childNodes);
    } else {
      target.appendChild(sanitized);
    }
  }
}

/** Keep only & entities, <b>, <i>, and <span class="..."> markup. */
export function sanitizeAppNameHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, '').trim();
  }

  const doc = new DOMParser().parseFromString(`<div id="root">${html}</div>`, 'text/html');
  const root = doc.getElementById('root');
  if (!root) return html.replace(/<[^>]*>/g, '').trim();

  const out = document.createElement('div');
  appendSanitizedChildren(root, out);
  return out.innerHTML;
}

/** Plain text for alt text, document title, and other non-HTML contexts. */
export function stripHtml(html: string): string {
  if (!html) return '';
  if (typeof document === 'undefined') {
    return html.replace(/<[^>]*>/g, '').trim() || html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || html;
}
